import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useScroll, useTransform, motion, AnimatePresence } from 'motion/react';
import { Loader2 } from 'lucide-react';
import { ReactLenis, useLenis } from 'lenis/react';

import Navbar from './components/Navbar';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
const ContactPage = lazy(() => import('./pages/ContactPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const OrderPage = lazy(() => import('./pages/OrderPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const ProductPage = lazy(() => import('./pages/ProductPage'));
const IndustryPage = lazy(() => import('./pages/IndustryPage'));
const CatalogPage = lazy(() => import('./pages/CatalogPage'));
const QuoteGenerator = lazy(() => import('./pages/QuoteGenerator'));

const PageLoader = () => (
  <motion.div 
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-white"
  >
    <div className="relative flex items-center justify-center w-20 h-20 mb-4">
      <Loader2 className="w-10 h-10 text-red-600 animate-spin absolute" />
      <div className="w-16 h-16 rounded-full border-2 border-red-100 border-t-red-600 animate-[spin_2s_linear_infinite]" />
    </div>
    <span className="text-sm font-semibold tracking-[0.2em] text-slate-400 uppercase animate-pulse">Loading</span>
  </motion.div>
);

const PageTransition = ({ children }: { children: React.ReactNode }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, scale: 0.98 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="flex-1 w-full"
    >
      {children}
    </motion.div>
  );
};

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <Suspense fallback={<PageLoader />}>
      <AnimatePresence mode="wait">
        {/* @ts-ignore - React key prop typing issue */}
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<PageTransition><HomePage /></PageTransition>} />
          <Route path="/about" element={<PageTransition><AboutPage /></PageTransition>} />
          <Route path="/contact" element={<PageTransition><ContactPage /></PageTransition>} />
          <Route path="/order" element={<PageTransition><OrderPage /></PageTransition>} />
          <Route path="/admin" element={<PageTransition><AdminPage /></PageTransition>} />
          <Route path="/products/:productId" element={<PageTransition><ProductPage /></PageTransition>} />
          <Route path="/industries/:industryId" element={<PageTransition><IndustryPage /></PageTransition>} />
          <Route path="/catalog" element={<PageTransition><CatalogPage /></PageTransition>} />
          <Route path="/quote-generator" element={<PageTransition><QuoteGenerator /></PageTransition>} />
        </Routes>
      </AnimatePresence>
    </Suspense>
  );
};

const AnimatedBackground = () => {
  return (
    <div className="fixed inset-0 z-[-1] overflow-hidden pointer-events-none bg-slate-50">
      {/* Static pattern background instead of heavy scroll-linked animations for better performance */}
      <div 
        className="absolute inset-0 z-0 opacity-40 md:opacity-70"
        style={{ 
          backgroundImage: 'radial-gradient(#64748b 1px, transparent 1px)',
          backgroundSize: '32px 32px'
        }}
      />
      {/* Static gradient blobs to keep the visual identity without destroying GPU performance */}
      <div className="hidden md:block absolute top-[0%] left-[-10%] w-[600px] h-[600px] rounded-full bg-red-300/20 blur-[120px] z-10" />
      <div className="absolute top-[20%] right-[-5%] w-[400px] md:w-[800px] h-[400px] md:h-[800px] rounded-full bg-orange-300/10 md:bg-orange-300/20 blur-[80px] md:blur-[140px]" />
      <div className="hidden md:block absolute bottom-[-10%] left-[20%] w-[900px] h-[900px] rounded-full bg-rose-300/10 blur-[140px]" />
    </div>
  );
};

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  const lenis = useLenis();

  useEffect(() => {
    if (lenis) {
      if (!hash) {
        lenis.scrollTo(0, { immediate: true });
      } else {
        const id = hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          lenis.scrollTo(element, { offset: -100 });
        }
      }
    } else {
      if (!hash) {
        document.documentElement.style.scrollBehavior = 'auto';
        window.scrollTo(0, 0);
        setTimeout(() => {
          document.documentElement.style.scrollBehavior = 'smooth';
        }, 10);
      } else {
        const id = hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  }, [pathname, hash, lenis]);

  return null;
};

export default function App() {
  useEffect(() => {
    document.title = "Buy Premium Bearings at Best Prices!";
  }, []);

  return (
    <Router>
      <ReactLenis root options={{ lerp: 0.15, wheelMultiplier: 0.9, smoothWheel: true }}>
        <ScrollToTop />
        <div className="min-h-screen font-sans text-slate-900 selection:bg-red-600 selection:text-white overflow-x-hidden flex flex-col relative z-0">
          <AnimatedBackground />
          <Navbar />
          <main className="flex-1 flex flex-col relative min-h-[60vh]">
            <AnimatedRoutes />
          </main>
          <Footer />
        </div>
      </ReactLenis>
    </Router>
  );
}

